import type { RenderManifestField } from './kryspaceRenderManifest';

export const ARTIFACT_DETAIL_SCHEMA = 'artifact-detail/v0.1';
export const ARTIFACT_ITEM_DETAIL_SCHEMA = 'artifact-item-detail/v0.1';

export interface EntityDetailStateMachine {
  states: string[];
  currentIndex: number;
}

export interface EntityDetailChildItem {
  itemId: string;
  name: string;
  currentState: string;
  eligible?: boolean;
}

export interface EntityDetailRelationship {
  type: string;
  targetItemId: string;
  targetLabel?: string | null;
}

export interface EntityDetailStateHistoryEntry {
  state: string;
  enteredAt?: string | null;
  exitedAt?: string | null;
  transitionMethod?: string | null;
}

export interface ArtifactItemDetailPayload {
  schemaVersion: typeof ARTIFACT_ITEM_DETAIL_SCHEMA;
  itemId: string;
  name: string;
  artifactTypeRef: string;
  currentState: string;
  executionSummary?: {
    itemLabel: string;
    actionLabel: string;
    contextLabel: string;
  };
  fields: RenderManifestField[];
  stateHistory?: EntityDetailStateHistoryEntry[];
  workspaceSlug?: string;
  assignedResourceRef?: string;
  assignedResourceLabel?: string;
  parentArtifactInstanceId?: string;
  parentArtifactLabel?: string;
  blockedReason?: string;
  dependsOnRefs?: string[];
  relatedIssueRefs?: string[];
  /** Subject / tracked artifact_item the activity references. */
  referencedItem?: ArtifactItemDetailPreview;
  referencedArtifactInstanceId?: string;
  referencedArtifactLabel?: string;
}

export interface ArtifactItemDetailPreview {
  itemId: string;
  name: string;
  artifactTypeRef: string;
  currentState: string;
  fields?: RenderManifestField[];
  executionSummary?: {
    itemLabel: string;
    actionLabel: string;
    contextLabel: string;
  };
}

export interface EntityDetailChildCompanyArtifact {
  artifactInstanceId: string;
  name: string;
  artifactTypeRef: string;
  currentState: string;
}

export interface ArtifactDetailPayload {
  schemaVersion: typeof ARTIFACT_DETAIL_SCHEMA;
  artifactInstanceId: string;
  name: string;
  artifactTypeRef: string;
  currentState: string;
  domainLabel?: string;
  fields: RenderManifestField[];
  stateMachine?: EntityDetailStateMachine;
  childItems: EntityDetailChildItem[];
  childCompanyArtifacts?: EntityDetailChildCompanyArtifact[];
  relationships: EntityDetailRelationship[];
  workspaceSlug?: string;
}

export function isArtifactDetailPayload(value: unknown): value is ArtifactDetailPayload {
  if (!value || typeof value !== 'object') return false;
  const row = value as ArtifactDetailPayload;
  return (
    row.schemaVersion === ARTIFACT_DETAIL_SCHEMA &&
    typeof row.artifactInstanceId === 'string' &&
    typeof row.name === 'string' &&
    Array.isArray(row.fields) &&
    Array.isArray(row.childItems)
  );
}

export function isArtifactItemDetailPayload(value: unknown): value is ArtifactItemDetailPayload {
  if (!value || typeof value !== 'object') return false;
  const row = value as ArtifactItemDetailPayload;
  return (
    row.schemaVersion === ARTIFACT_ITEM_DETAIL_SCHEMA &&
    typeof row.itemId === 'string' &&
    typeof row.name === 'string' &&
    Array.isArray(row.fields)
  );
}
